import { v4 as uuid } from 'uuid';

import type { ShowToastOptions, ToastRecord } from './Toast.common';

/** Still mounted for the length of its exit, but holding no slot and not replaceable in place. */
export const isLeaving = (toast: ToastRecord): boolean =>
  Boolean(toast.isExiting || toast.isDismissing);

/**
 * Appends a toast, or replaces a same-`key` one in place, keeping its `id` and restarting its
 * timer. A match that is already leaving does not count: its `id` belongs to an exit in flight.
 */
export const addToast = (
  toasts: readonly ToastRecord[],
  options: ShowToastOptions
): { toasts: ToastRecord[]; id: string } => {
  const existingIndex =
    options.key === undefined
      ? -1
      : toasts.findIndex((toast) => toast.key === options.key && !isLeaving(toast));

  if (existingIndex === -1) {
    const record: ToastRecord = { ...options, id: uuid(), timerEpoch: 0 };
    return { toasts: [...toasts, record], id: record.id };
  }

  const existing = toasts[existingIndex];
  const replacement: ToastRecord = {
    ...options,
    id: existing.id,
    timerEpoch: existing.timerEpoch + 1,
  };
  const next = [...toasts];
  next[existingIndex] = replacement;
  return { toasts: next, id: existing.id };
};

/** Flags a toast as leaving. Returns the same list when unchanged, so a repeat costs no render. */
export const markExiting = (toasts: readonly ToastRecord[], id: string): readonly ToastRecord[] => {
  if (!toasts.some((toast) => toast.id === id && !toast.isExiting)) return toasts;

  return toasts.map((toast) => (toast.id === id ? { ...toast, isExiting: true } : toast));
};

/** Flags a toast for the stack to dismiss, so it leaves through its exit. */
export const requestDismiss = (toasts: readonly ToastRecord[], id: string): ToastRecord[] =>
  toasts.map((toast) => (toast.id === id ? { ...toast, isDismissing: true } : toast));

export const removeToast = (toasts: readonly ToastRecord[], id: string): ToastRecord[] =>
  toasts.filter((toast) => toast.id !== id);

/**
 * The oldest toasts pushed past the cap, which a stack sends on an accelerated exit. One already
 * leaving is not counted: it would cost a survivor its place to make room for an exit in flight.
 */
export const getEvictionCandidates = (
  toasts: readonly ToastRecord[],
  maxVisible: number
): ToastRecord[] => {
  const staying = toasts.filter((toast) => !isLeaving(toast));

  return staying.slice(0, Math.max(0, staying.length - maxVisible));
};

/**
 * Everything the cap has not evicted, oldest first. One already leaving is still in here: it is
 * on screen for the length of its exit, and the stack reads its neighbors out of this list.
 */
export const getVisibleToasts = (
  toasts: readonly ToastRecord[],
  maxVisible: number
): ToastRecord[] => {
  const evicted = new Set(getEvictionCandidates(toasts, maxVisible).map((toast) => toast.id));

  return toasts.filter((toast) => !evicted.has(toast.id));
};
